/**
 * CinematicHero
 * -----------------------------------------------------------
 * Public-facing page header that ports the locked R5-M1 mock
 * styling into the production app. Layered navy scrim sits
 * over the body's fixed cinematic photo backdrop with a brass-
 * bordered chip bar at the bottom.
 *
 * Usage:
 *   <CinematicHero
 *     eyebrow="· Welcome Aboard ·"
 *     title="Lantern Atlas"
 *     dropCap="L"
 *     lede="Good evening, Captain Perry. The lanterns are lit…"
 *     bar={<>
 *       <strong>Salted Lantern</strong> live shanties · open till 11pm
 *     </>}
 *     featurePlate={<FeaturePlate ... />}
 *   />
 *
 * Slots:
 *   - eyebrow:      small uppercase brass label (string or node)
 *   - title:        h1 text — pass `dropCap` to render the first
 *                   letter as an ornamental Cormorant cap; the
 *                   `dropCap` letter is stripped from the start
 *                   of `title` automatically when matched.
 *   - lede:         Cormorant lede paragraph
 *   - bar:          chip-bar children (any node)
 *   - featurePlate: when present, hero uses --with-card layout;
 *                   otherwise hero is centered.
 *   - bgImage:      optional CSS background-image override (eg
 *                   `url(/assets/foo.jpg)`); defaults to the
 *                   transparent body-photo passthrough.
 */
import React from 'react';

export function CinematicHero({
  eyebrow,
  title,
  dropCap,
  lede,
  bar,
  featurePlate,
  bgImage,
  className = '',
  ...rest
}) {
  const variant = featurePlate ? 'r5-hero--with-card' : 'r5-hero--centered';
  const cls = ['r5-hero', variant, className].filter(Boolean).join(' ');
  const bgStyle = bgImage ? { backgroundImage: bgImage } : undefined;

  // Drop-cap: explicit <span class="cap">L</span> wrapper (locked Option B).
  // If `dropCap` matches the first character of `title`, strip it from the
  // remainder so the rendered output is `<span class="cap">L</span>antern`.
  let titleNode;
  if (dropCap && typeof title === 'string') {
    const tail =
      title.charAt(0).toLowerCase() === dropCap.toLowerCase()
        ? title.slice(1)
        : title;
    titleNode = (
      <>
        <span className="cap">{dropCap}</span>
        {tail}
      </>
    );
  } else {
    titleNode = title;
  }

  return (
    <header className={cls} {...rest}>
      <div className="r5-hero__bg" style={bgStyle} aria-hidden="true" />
      <div className="r5-hero__scrim" aria-hidden="true" />
      <div className="r5-hero__main">
        {eyebrow && <span className="r5-hero__eyebrow">{eyebrow}</span>}
        {title && <h1 className="r5-hero__title">{titleNode}</h1>}
        {lede && <p className="r5-hero__lede">{lede}</p>}
        {bar && (
          <span className="r5-hero__bar" aria-label="Page summary">
            {bar}
          </span>
        )}
      </div>
      {featurePlate}
    </header>
  );
}

export default CinematicHero;
